import React, { useState, useRef } from 'react';
import {
  Server,
  FileKey,
  ShieldCheck,
  ShieldAlert,
  Save,
  Upload,
  CheckCircle2,
  AlertCircle,
  Activity,
  Building2,
} from 'lucide-react';


import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  useComputedColorScheme
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

import NetworkConfiguration from './NetworkConfiguration';
import useConfig from "./configProvider.js";

export default function OnPremConfig({params}) {
  const {phase} = params;

  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, success
  const [licenseFile, setLicenseFile] = useState(null);
  const [licenseError, setLicenseError] = useState(null);
  const fileInputRef = useRef(null);
  const {data: configData, mutate: refreshConfig} = useConfig();

  // License state is derived from /api/config (clientConfig.license),
  // which the backend unpacks from the licenseString stored in the
  // configuration file.
  const license = configData?.license;
  const isLicensed = !!license && Object.keys(license).length > 0;

  // Network settings are only shown/editable during initial setup. Once the
  // instance is configured (GitHub Apps installed), changes would break
  // existing installations, so the card is hidden entirely.
  const isInstalled = configData?.mode === 'installed';
  const isConfigured = !!configData?.mode && configData.mode !== 'setup';

  const [config, setConfig] = useState({
    orgName: 'RSJ Software GmbH',
  });

  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }, 1000);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    setLicenseError(null);

    try {
      const text = await file.text();
      const rsp = await fetch('/api/license', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseString: text.trim() }),
      });

      if (!rsp.ok) {
        setLicenseError((await rsp.text()) || 'Upload failed');
        return;
      }

      setLicenseFile(file.name);
      refreshConfig();
    } catch (err) {
      setLicenseError(err.message);
    }
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="md">
        <Stack gap="lg">
          <Paper radius="lg" p="lg" withBorder>
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
              <Group gap="md" align="center">
                <ThemeIcon size="xl" radius="md" variant="light" color="blue">
                  <Server size={20} />
                </ThemeIcon>
                <Stack gap={4}>
                  <Title order={1} fz="1.5rem" fw={800}>
                    {t('Timestamp GIT')}
                  </Title>
                  <Badge variant="light" color="blue">
                    {t('On-Premises Instance')}
                  </Badge>
                </Stack>
              </Group>

              <Group gap="xs">
                <Badge variant="light" color="teal" leftSection={<Activity size={12} />}>
                  {t('System Online')}
                </Badge>
                <Badge variant="outline" color="gray">
                  {configData?.version}
                </Badge>
              </Group>
            </Group>
          </Paper>

          <Paper
            radius="lg"
            p="lg"
            withBorder
            bg={
              isLicensed
                ? 'teal.0'
                : colorScheme === 'dark'
                  ? 'rgba(255, 146, 43, 0.14)'
                  : 'orange.0'
            }
          >
            <Group justify="space-between" align="center" wrap="wrap">
              <Group gap="md">
                <ThemeIcon
                  size="lg"
                  radius="xl"
                  variant="light"
                  color={isLicensed ? 'teal' : 'orange'}
                >
                  {isLicensed ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={700}>
                    {isLicensed
                      ? t('Enterprise License Active')
                      : t('Trial Mode Active')}
                  </Text>
                  <Text fz="sm" c="dimmed">
                    {isLicensed
                      ? t('Registered to RSJ Software GmbH')
                      : t('Limited feature set. Please upload a license file to unlock.')}
                  </Text>
                </Stack>
              </Group>

              {!isLicensed && (
                <Badge variant="filled" color="orange">
                  {t('Demo')}
                </Badge>
              )}
            </Group>
          </Paper>

          {!isInstalled && (
            <NetworkConfiguration phase={phase} editable={!isConfigured}/>
          )}

          <Paper radius="lg" p="lg" withBorder>
            <Stack gap="md">
              <Group gap="sm" align="center">
                <ThemeIcon variant="light" color="violet" radius="xl" size="md">
                  <FileKey size={16} />
                </ThemeIcon>
                <Title order={2} fz="1rem" fw={700}>
                  {t('License Management')}
                </Title>
              </Group>

              <input
                type="file"
                ref={fileInputRef}
                hidden
                onChange={handleFileUpload}
                accept=".lic,.json"
              />

              {isLicensed ? (
                <Stack align="center" gap="xs" py="xs">
                  <ThemeIcon size="xl" color="teal" variant="light" radius="xl">
                    <CheckCircle2 size={20} />
                  </ThemeIcon>
                  <Text fw={700}>
                    {t('Enterprise License Applied')}
                  </Text>
                  {license?.company && (
                    <Text fz="sm" c="dimmed">
                      {license.company}
                    </Text>
                  )}
                  <Button
                    variant="outline"
                    color="gray"
                    size="sm"
                    mt="xs"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('Replace File')}
                  </Button>
                </Stack>
              ) : (
                <Stack gap="xs">
                  <Button
                    variant={licenseFile ? 'filled' : 'light'}
                    color={licenseFile ? 'teal' : 'blue'}
                    size="md"
                    leftSection={<Upload size={16} />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {licenseFile
                      ? t('Ready: {{file}}', { file: licenseFile })
                      : t('Upload .lic file')}
                  </Button>
                  <Text fz="xs" c="dimmed">
                    {t('Convert your demo instance to full Enterprise support.')}
                  {licenseError && (
                    <Text fz="sm" c="red">
                      {licenseError}
                    </Text>
                  )}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper radius="lg" p="lg" withBorder>
            <Stack gap="md">
              <Group gap="sm" align="center">
                <ThemeIcon variant="light" color="teal" radius="xl" size="md">
                  <Building2 size={16} />
                </ThemeIcon>
                <Title order={2} fz="1rem" fw={700}>
                  {t('Organization Info')}
                </Title>
              </Group>

              <TextInput
                maw={500}
                label={t('Internal Org Name')}
                description={t('Appears on local audit PDF exports.')}
                leftSection={<Building2 size={16} />}
                value={config.orgName}
                onChange={(e) => setConfig({ ...config, orgName: e.target.value })}
              />
            </Stack>
          </Paper>

          <Group justify="flex-end">
            <Button
              variant="filled"
              color={saveStatus === 'success' ? 'teal' : 'blue'}
              disabled={saveStatus === 'saving'}
              onClick={handleSave}
              size="md"
              leftSection={<Save size={16} />}
            >
              {saveStatus === 'saving'
                ? t('Applying...')
                : saveStatus === 'success'
                  ? t('Settings Applied')
                  : t('Save Configuration')}
            </Button>
          </Group>
          
        </Stack>
      </Container>
    </Box>
  );
}
